import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Container,
  Field,
  Heading,
  Row,
  PageContent,
  Select,
  Datepicker,
} from 'components';
import { globalPropTypes } from 'helpers';
import moment from 'moment';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  typeOptions,
  holderAuthorizeOptions,
  visibilityOptions,
  yesNoBoolOptions,
} from '../../CisOptions';
import { getSignatureOptions } from '../../../../api/services/cis';
import { useMemo } from 'react';

function EditCertificate({
  state,
  dispatch,
  updateFieldCreator,
  errors,
  updateCertificate,
  removeError,
  levelDomains,
  certificationBodies,
  loadCertificatesAttributes,
  type,
  organizations,
  memberAssociations,
  loadClientMemberAssociations,
  languages,
}) {
  const { t } = useTranslation();
  const history = useHistory();

  const gcOrgs = useMemo(() => organizations.filter((org) => org.type?.toLowerCase() === 'gc'), [
    organizations,
  ]);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    if (name === 'memberAssociation') {
      dispatch(updateFieldCreator({ name: 'otherSignatures', value: [] }));
      dispatch(updateFieldCreator({ name: 'headSignature', value: null }));
      dispatch(updateFieldCreator({ name: 'assessorSignature', value: null }));
    }

    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  useEffect(() => {
    if (state.certificationBody) {
      loadClientMemberAssociations(state.certificationBody);
    }
  }, [state.certificationBody]);

  useEffect(() => {
    if (state.memberAssociation) {
      getSignatureOptions({
        memberAssociation: state.memberAssociation,
      }).then((signatureOptions) => {
        signatureOptions.json().then((res) => {
          const options = res.results.result.map((item) => ({
            label: item.user?.firstName + ' ' + item.user?.lastName,
            value: item?.id,
            assessorType: item?.assessorType,
          }));

          if (
            state.headSignature &&
            options.findIndex((opt) => opt.value === state.headSignature) === -1
          ) {
            options.push({
              label: [
                state.fullHeadSignature?.user?.firstName,
                state.fullHeadSignature?.user?.lastName,
              ]
                .filter(Boolean)
                .join(' '),
              value: state.fullHeadSignature?.id,
              assessorType: state.fullHeadSignature?.assessorType,
            });
          }
          if (
            state.assessorSignature &&
            options.findIndex((opt) => opt.value === state.assessorSignature) === -1
          ) {
            options.push({
              label: [
                state.fullAssessorSignature?.user?.firstName,
                state.fullAssessorSignature?.user?.lastName,
              ]
                .filter(Boolean)
                .join(' '),
              value: state.fullAssessorSignature?.id,
              assessorType: state.fullAssessorSignature?.assessorType,
            });
          }
          dispatch(updateFieldCreator({ name: 'signatureOptions', value: options }));
        });
      });
    }
  }, [state.memberAssociation, dispatch]);

  const submit = () => {
    const body = {
      certificateHolder: state.certificateHolder,
      certificateLevelDomain: state.certificateLevelDomain,
      issueDate: state.issueDate ? moment(state.issueDate).format('YYYY-MM-DD') : null,
      type: state.type,
      validUntil: state.validUntil ? moment(state.validUntil).format('YYYY-MM-DD') : null,
      certId: state.certId,
      holderAuthorized:
        state.holderAuthorized === true ? 1 : state.holderAuthorized === false ? 0 : null,
      visibility: state.visibility === true ? 1 : state.visibility === false ? 0 : null,
      organization: state.organization,
      memberAssociation: state.memberAssociation,
      headSignature: state.headSignature,
      assessorSignature: state.assessorSignature,
      language: state.language,
    };
    if (type !== 'ma') {
      body.certificationBody = state.certificationBody;
    }
    if (type === 'ipma') {
      body.isHistorical =
        state.isHistorical === true ? 1 : state.isHistorical === false ? 0 : null;
    }

    updateCertificate(state.id, body, history);
  };

  useEffect(() => {
    loadCertificatesAttributes();
  }, []);

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.certificates.editCertificate')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="certificateLevelDomain"
                  label={t('page.user.cis.certificates.levelDomain')}
                  value={
                    levelDomains.find((option) => option?.value === state.certificateLevelDomain) ||
                    ''
                  }
                  clear={() => handleChangeSelect('certificateLevelDomain')(null)}
                  required
                  error={errors.certificateLevelDomain}
                >
                  <Select
                    id="certificateLevelDomain"
                    type="text"
                    placeholder={t('page.user.cis.certificates.levelDomainPlaceholder')}
                    value={
                      levelDomains.find(
                        (option) => option?.value === state.certificateLevelDomain,
                      ) || ''
                    }
                    options={levelDomains}
                    onChange={handleChangeSelect(
                      'certificateLevelDomain',
                      'certificateLevelDomain',
                    )}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="issueDate"
                  label={t('page.user.cis.certificates.issueDate')}
                  value={state.issueDate}
                  clear={() => handleChangeCustom('issueDate')(null)}
                  required
                  error={errors.issueDate}
                >
                  <Datepicker
                    id="issueDate"
                    type="text"
                    placeholderText={t('page.user.cis.certificates.issueDatePlaceholder')}
                    selected={state.issueDate}
                    onChange={handleChangeCustom('issueDate', 'issueDate')}
                    dateFormat="yyyy-MM-dd"
                    maxDate={new Date()}
                    minDate={type === 'ma' ? moment().subtract(2, 'months').toDate() : null}
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="visibility"
                  label={t('page.user.cis.certificates.visibility')}
                  value={
                    visibilityOptions.find((option) => option?.value === state.visibility) || ''
                  }
                  clear={() => handleChangeSelect('visibility')(null)}
                  error={errors.visibility}
                >
                  <Select
                    id="visibility"
                    type="text"
                    placeholder={t('page.user.cis.certificates.visibilityPlaceholder')}
                    value={
                      visibilityOptions.find((option) => option?.value === state.visibility) || ''
                    }
                    options={visibilityOptions}
                    onChange={handleChangeSelect('visibility', 'visibility')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="type"
                  label={t('page.user.cis.certificates.type')}
                  value={
                    typeOptions.find(
                      (option) => option?.value?.toLowerCase() === state.type?.toLowerCase(),
                    ) || ''
                  }
                  clear={() => handleChangeSelect('type')(null)}
                  error={errors.type}
                >
                  <Select
                    id="certificateType"
                    type="text"
                    placeholder={t('page.user.cis.certificates.typePlaceholder')}
                    value={
                      typeOptions.find(
                        (option) => option?.value?.toLowerCase() === state.type?.toLowerCase(),
                      ) || ''
                    }
                    options={typeOptions}
                    onChange={handleChangeSelect('type', 'type')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="validUntil"
                  label={t('page.user.cis.certificates.validUntil')}
                  value={state.validUntil}
                  clear={() => handleChangeCustom('validUntil')(null)}
                  required
                  error={errors.validUntil}
                >
                  <Datepicker
                    id="validUntil"
                    type="text"
                    placeholderText={t('page.user.cis.certificates.issueDatePlaceholder')}
                    selected={state.validUntil}
                    onChange={handleChangeCustom('validUntil', 'validUntil')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    minDate={state.issueDate}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="certId"
                  label={t('page.user.cis.certificates.certificateNumber')}
                  required
                  error={errors.certId}
                >
                  <input
                    id="certId"
                    type="text"
                    placeholder={t('page.user.cis.certificates.certificateNumberPlaceholder')}
                    value={state.certId}
                    onChange={handleChangeNative('certId', 'certId')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="certificationBody"
                  label={t('page.user.cis.certificates.certificationBody')}
                  value={
                    certificationBodies.find(
                      (option) => option?.value === state.certificationBody,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('certificationBody')(null)}
                  required
                  error={errors.certificationBody}
                >
                  <Select
                    id="certificationBody"
                    type="text"
                    placeholder={t('page.user.cis.certificates.certificationBodyPlaceholder')}
                    value={
                      certificationBodies.find(
                        (option) => option?.value === state.certificationBody,
                      ) || ''
                    }
                    options={certificationBodies}
                    onChange={handleChangeSelect('certificationBody', 'certificationBody')}
                    disabled={type === 'ma'}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="memberAssociation"
                  label={t('page.user.cis.certificates.clientMemberAssociation')}
                  value={
                    memberAssociations.find(
                      (option) => option?.value === state.memberAssociation,
                    ) || ''
                  }
                  required
                  clear={() => handleChangeSelect('memberAssociation')(null)}
                  error={errors.memberAssociation}
                >
                  <Select
                    id="memberAssociation"
                    type="text"
                    placeholder={t('page.user.cis.certificates.clientMemberAssociationPlaceholder')}
                    value={
                      memberAssociations.find(
                        (option) => option?.value === state.memberAssociation,
                      ) || ''
                    }
                    options={memberAssociations}
                    onChange={handleChangeSelect('memberAssociation', 'memberAssociation')}
                    disabled={
                      !state.memberAssociation ||
                      !memberAssociations ||
                      memberAssociations.length === 0
                    }
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="organization"
                  label={t('page.user.cis.certificates.globalCustomerName')}
                  value={gcOrgs.find((option) => option?.value === state.organization) || ''}
                  clear={() => handleChangeSelect('organization')(null)}
                  error={errors.organization}
                >
                  <Select
                    id="organization"
                    type="text"
                    placeholder={t('page.user.cis.certificates.globalCustomerNamePlaceholder')}
                    value={gcOrgs.find((option) => option?.value === state.organization) || ''}
                    options={gcOrgs}
                    onChange={handleChangeSelect('organization', 'organization')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  required={type === 'ma'}
                  id="headSignature"
                  label={t('page.user.cis.certificates.headSignature')}
                  error={errors.headSignature}
                  value={
                    state.signatureOptions.find((option) => option?.value === state.headSignature) ||
                    ''
                  }
                  clear={() => handleChangeSelect('headSignature')(null)}
                >
                  <Select
                    id="headSignature"
                    name="headSignature"
                    placeholder={t('page.user.cis.certificates.selectSignaturePlaceholder')}
                    options={state.signatureOptions.filter(
                      (option) =>
                        option?.value !== state.assessorSignature &&
                        option?.assessorType === 'H' &&
                        !state.otherSignatures.includes(option?.value),
                    )}
                    disabled={
                      state.signatureOptions?.length === 0 || state.memberAssociation === null
                    }
                    value={
                      state.signatureOptions.find(
                        (option) => option?.value === state.headSignature,
                      ) || ''
                    }
                    onChange={handleChangeSelect('headSignature', 'headSignature')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  required={type === 'ma'}
                  id="assessorSignature"
                  label={t('page.user.cis.certificates.assessorSignature')}
                  error={errors.assessorSignature}
                  value={
                    state.signatureOptions.find(
                      (option) => option?.value === state.assessorSignature,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('assessorSignature')(null)}
                >
                  <Select
                    id="assessorSignature"
                    name="assessorSignature"
                    placeholder={t('page.user.cis.certificates.selectSignaturePlaceholder')}
                    options={state.signatureOptions.filter(
                      (option) =>
                        option?.value !== state.headSignature &&
                        option?.assessorType === 'S' &&
                        !state.otherSignatures.includes(option?.value),
                    )}
                    disabled={
                      state.signatureOptions?.length === 0 || state.memberAssociation === null
                    }
                    value={
                      state.signatureOptions.find(
                        (option) => option?.value === state.assessorSignature,
                      ) || ''
                    }
                    onChange={handleChangeSelect('assessorSignature', 'assessorSignature')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  required
                  id="language"
                  label={t('page.user.cis.certificates.language')}
                  value={languages.find((option) => option?.value === state.language) || ''}
                  clear={() => handleChangeSelect('language')(null)}
                  error={errors.language}
                >
                  <Select
                    id="language"
                    type="text"
                    placeholder={t('page.user.cis.certificates.languagePlaceholder')}
                    value={languages.find((option) => option?.value === state.language) || ''}
                    options={languages}
                    onChange={handleChangeSelect('language', 'language')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="holderAuthorized"
                  label={t('page.user.cis.certificates.holderAuthorized')}
                  value={
                    holderAuthorizeOptions.find(
                      (option) => option?.value === state.holderAuthorized,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('holderAuthorized')(null)}
                  error={errors.holderAuthorized}
                >
                  <Select
                    id="holderAuthorized"
                    type="text"
                    placeholder={t('page.user.cis.certificates.holderAuthorizedPlaceholder')}
                    value={
                      holderAuthorizeOptions.find(
                        (option) => option?.value === state.holderAuthorized,
                      ) || ''
                    }
                    options={holderAuthorizeOptions}
                    onChange={handleChangeSelect('holderAuthorized', 'holderAuthorized')}
                  />
                </Field>
              </Margin>
            </Col>
            {type === 'ipma' && (
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="isHistorical"
                    label="Historical"
                    value={
                      yesNoBoolOptions.find((option) => option?.value === state.isHistorical) || ''
                    }
                    clear={() => handleChangeSelect('isHistorical')(null)}
                    error={errors.isHistorical}
                  >
                    <Select
                      id="isHistorical"
                      type="text"
                      placeholder={t('global.selectPlaceholder')}
                      value={
                        yesNoBoolOptions.find((option) => option?.value === state.isHistorical) ||
                        ''
                      }
                      options={yesNoBoolOptions}
                      onChange={handleChangeSelect('isHistorical', 'isHistorical')}
                    />
                  </Field>
                </Margin>
              </Col>
            )}
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
}

EditCertificate.propTypes = {
  state: PropTypes.shape({
    id: PropTypes.number,
    certificateHolder: PropTypes.number,
    certificateLevelDomain: PropTypes.number,
    certificationBody: PropTypes.number,
    issueDate: PropTypes.any,
    type: PropTypes.string,
    validUntil: PropTypes.any,
    certId: PropTypes.string,
    holderAuthorized: PropTypes.bool,
    visibility: PropTypes.bool,
    organization: PropTypes.number,
    isHistorical: PropTypes.bool,
  }),
  dispatch: PropTypes.func.isRequired,
  updateFieldCreator: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    certificateHolder: PropTypes.string,
    certificateLevelDomain: PropTypes.string,
    certificationBody: PropTypes.string,
    issueDate: PropTypes.string,
    type: PropTypes.string,
    validUntil: PropTypes.string,
    certId: PropTypes.string,
    holderAuthorized: PropTypes.string,
    visibility: PropTypes.string,
    isHistorical: PropTypes.string,
  }),
  updateCertificate: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  certificationBodies: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadCertificatesAttributes: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['ipma', 'ma']),
};

EditCertificate.defaultProps = {
  errors: {},
  type: 'ipma',
};

export default EditCertificate;
